import * as React from "react"
import { useDebouncedCallback, type CollectionSource, type QueryableCollection } from "@florisoft/framework"
import { Button } from "./button"
import { useUiTranslation } from "../../localization/use-ui-translation"

/**
 * Search done by the collection source: the text the user types is turned into a query that the source applies and
 * loads again.
 */
export type CollectionSourceSearch<TQuery> = {
  /** Marks a search the source does. */
  mode: "source"
  /** Builds the next query from the typed text and the current query of the source. */
  query: (text: string, current: Readonly<TQuery>) => TQuery
}
/**
 * Search done in the control: it filters the items already loaded by the typed text, so it only works for a collection
 * that is loaded completely and has no more items than `maxItems`.
 */
export type CollectionLocalSearch = {
  /** Marks a search the control does. */
  mode: "local"
}
export type CollectionSearch<TQuery> = CollectionSourceSearch<TQuery> | CollectionLocalSearch

export type CollectionOptionsProps<TItem, TQuery = void> = {
  /** The content of an option row. Defaults to the item's `getLabel` text. */
  renderItem?: (item: TItem) => React.ReactNode
  /** The most options the list shows at once. Defaults to 200. */
  maxItems?: number
  /** How long typing pauses, in milliseconds, before a search by the source runs. Defaults to 250. */
  debounceMs?: number
  /** How many characters must be typed before the options are searched. Defaults to 0. */
  minSearchLength?: number
} & (
  | { source?: undefined; getLabel?: (item: TItem) => string; search?: never }
  | {
      /** The collection the options come from, such as a fake or a remote collection. */
      source: CollectionSource<TItem>
      /** The text of an item: its option label, the selected value's text and what a local search matches. */
      getLabel: (item: TItem) => string
      /**
       * `{ mode: "local" }` filters the loaded items by the typed text; `{ mode: "source", query }` lets the source
       * search.
       */
      search?: CollectionLocalSearch
    }
  | {
      source: CollectionSource<TItem> & QueryableCollection<TQuery>
      getLabel: (item: TItem) => string
      search: CollectionSourceSearch<TQuery>
    }
)

/** Shared data behavior for existing controls; storage and transport remain source concerns. */
export function useCollectionOptions<TItem, TQuery>({
  source,
  search,
  open,
  debounceMs = 250,
  minSearchLength = 0,
  maxItems = 200,
}: {
  source?: CollectionSource<TItem>
  search?: CollectionSearch<TQuery>
  open: boolean
  debounceMs?: number
  minSearchLength?: number
  maxItems?: number
}) {
  const { t } = useUiTranslation()
  const [searchText, setSearchText] = React.useState("")
  const [appliedSearch, setAppliedSearch] = React.useState(() => ({ source, text: "" }))
  const subscribe = React.useCallback((listener: () => void) => source?.subscribe(listener) ?? (() => {}), [source])
  const snapshot = React.useCallback(() => source?.revision ?? 0, [source])
  const revision = React.useSyncExternalStore(subscribe, snapshot, snapshot)
  const limit = Math.max(1, Number.isFinite(maxItems) ? Math.floor(maxItems) : 200)
  const tooShort = Boolean(search && searchText.length < minSearchLength)
  const applySearch = (text: string) => {
    if (!source || search?.mode !== "source") return
    const queryable = source as CollectionSource<TItem> & QueryableCollection<TQuery>
    queryable.applyQuery(search.query(text, queryable.query))
    setAppliedSearch({ source, text })
  }
  // Source ranges are inclusive: [start, end].
  const debounce = useDebouncedCallback((text: string) => {
    if (!source || search?.mode !== "source" || text.length < minSearchLength) return
    applySearch(text)
    source.ensureRange(0, Math.min(source.pageSize, limit) - 1)
  }, debounceMs)
  const waiting =
    Boolean(source) &&
    search?.mode === "source" &&
    (source !== appliedSearch.source || searchText !== appliedSearch.text)
  React.useEffect(() => {
    if (open && !tooShort && waiting) debounce(searchText)
    else debounce.cancel()
    return debounce.cancel
  }, [open, tooShort, waiting, searchText, source, debounce])
  const localIncomplete = Boolean(
    source && search?.mode === "local" && (source.totalCount === undefined || source.loadedCount < source.totalCount)
  )
  const localTooLarge = Boolean(
    source && search?.mode === "local" && source.totalCount !== undefined && source.totalCount > limit
  )
  const localUnavailable =
    localTooLarge ||
    Boolean(source && search?.mode === "local" && source.totalCount === undefined && source.loadedCount > 0)
  React.useEffect(() => {
    if (!source || !open || tooShort || waiting) return
    source.ensureRange(0, Math.min(source.pageSize, limit) - 1)
    if (search?.mode === "local" && source.totalCount !== undefined && source.totalCount <= limit)
      source.ensureRange(0, source.totalCount - 1)
  }, [open, source, source?.totalCount, search?.mode, limit, tooShort, waiting])
  const items = React.useMemo(() => {
    const result: TItem[] = []
    if (!source || tooShort || waiting || localIncomplete || localTooLarge) return result
    for (let index = 0; index < Math.min(limit, source.itemCount); index++) {
      const item = source.get(index)
      if (item === undefined) break
      result.push(item)
    }
    return result
  }, [source, revision, tooShort, waiting, localIncomplete, localTooLarge, limit])
  const failedIndex =
    source?.status === "error" ? 0 : source?.getItemState(items.length) === "error" ? items.length : undefined
  const busy = !tooShort && !localUnavailable && Boolean(source?.isLoading || waiting || localIncomplete)
  const feedback = !source ? null : tooShort ? (
    <div role="status" className="px-4 py-2 text-ui-12">
      {t(($) => $.lookup.typeToSearch, { count: minSearchLength })}
    </div>
  ) : localUnavailable ? (
    <div role="alert" className="px-4 py-2 text-ui-12">
      {t(($) => $.lookup.localUnavailable)}
    </div>
  ) : failedIndex !== undefined ? (
    <div role="alert" className="px-4 py-2 text-ui-12">
      {t(($) => $.lookup.failed)}
      <Button
        variant="ghost"
        shape="rounded"
        onClick={() => {
          const range = source.getItemErrorRange(failedIndex)
          void source.retryRange(
            range?.start ?? failedIndex,
            range?.end ?? Math.min(failedIndex + source.pageSize, limit) - 1
          )
        }}
      >
        {t(($) => $.lookup.retry)}
      </Button>
    </div>
  ) : busy ? (
    <div role="status" className="px-4 py-2 text-ui-12">
      {t(($) => $.lookup.loading)}
    </div>
  ) : null
  const paging =
    source &&
    !busy &&
    failedIndex === undefined &&
    search?.mode !== "local" &&
    items.length < limit &&
    items.length < source.itemCount ? (
      <Button
        variant="ghost"
        shape="rounded"
        onClick={() => source.ensureRange(items.length, Math.min(items.length + source.pageSize, limit) - 1)}
      >
        {t(($) => $.lookup.loadMore)}
      </Button>
    ) : null
  return {
    items,
    busy,
    feedback,
    paging,
    empty: !feedback && !items.length,
    limitFeedback:
      source && search && items.length >= limit && source.itemCount > limit ? (
        <div role="status" className="px-4 py-2 text-ui-12">
          {t(($) => $.lookup.refineSearch)}
        </div>
      ) : null,
    setSearchText,
    resetSearch: () => {
      setSearchText("")
      debounce.cancel()
      if (minSearchLength === 0) applySearch("")
    },
    cancelSearch: debounce.cancel,
  }
}
